import {
  TripID,
  TripSearchID,
  TripSearchResult,
  TripSearchResultCabinGrade,
  TripSearchResultDeck,
  TripSearchResultID,
} from "@cruisen/contract"
import { tripsCollection } from "../firebase"
import { getSubcollectionDocs } from "./getSubcollectionDocs"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import debounce from "lodash.debounce"
import { CruiseSearchResultQuery } from "@cruisen/react-shared"
import {
  doc,
  collection,
  where,
  limit,
  query,
  getDocs,
  getDoc,
  onSnapshot,
} from "firebase/firestore"

export const getCruiseSearchResult = async (tripId: TripID, cruiseId: TripSearchResultID) => {
  if (!tripId || !cruiseId) return null

  // @TODO pass tripSearchId as an argument to remove extra query
  const tripSearchQuery = query(
    collection(doc(tripsCollection, tripId), "Searches"),
    where("isMainTripSearch", "==", true),
    limit(1),
  )

  const searchSnapshot = await getDocs(tripSearchQuery)

  if (searchSnapshot.empty) return null

  const searchDoc = searchSnapshot.docs[0]

  const cruiseSnapshot = await getDoc(doc(collection(searchDoc.ref, "Results"), cruiseId))

  const cruiseSearchResult = {
    id: cruiseSnapshot.id,
    ...cruiseSnapshot.data(),
  } as TripSearchResult

  return {
    tripSearchId: searchDoc.id,
    cruiseSearchResult,
  }
}

export const subscribeCruiseSearchResult = (
  tripId: TripID,
  tripSearchId: TripSearchID,
  cruiseId: TripSearchResultID,
  onResultsUpdate: (queryResult: CruiseSearchResultQuery) => void,
): (() => void) => {
  try {
    const cruiseRef = doc(
      collection(
        doc(collection(doc(tripsCollection, tripId), "Searches"), tripSearchId),
        "Results",
      ),
      cruiseId,
    )

    const unsubscribeResult = onSnapshot(cruiseRef, async (cruiseSnapshot) => {
      if (!cruiseSnapshot.exists()) {
        onResultsUpdate({ status: "notFound", cruiseSearchResult: null, tripSearchId })
        return () => unsubscribeResult()
      }

      const cruiseSearchResult = {
        id: cruiseSnapshot.id,
        ...cruiseSnapshot.data(),
      } as TripSearchResult

      const cabinGrades = await getSubcollectionDocs<TripSearchResultCabinGrade>(
        cruiseSnapshot.ref,
        "CabinGrades",
      )
      const decks = await getSubcollectionDocs<TripSearchResultDeck>(cruiseSnapshot.ref, "Decks")

      onResultsUpdate({ status: "success", cruiseSearchResult, cabinGrades, decks, tripSearchId })
      return () => unsubscribeResult()
    })

    return () => unsubscribeResult()
  } catch (error) {
    console.error(error)
    onResultsUpdate({ status: "error", cruiseSearchResult: null, tripSearchId: "" })
    return () => undefined
  }
}

export const useCruiseSearchResult = (
  tripId: TripID,
  tripSearchId: TripSearchID,
  cruiseId: TripSearchResultID,
) => {
  const queryClient = useQueryClient()
  const [isDebounceLoading, setIsDebounceLoading] = useState(false)

  const queryData = useQuery<CruiseSearchResultQuery>({
    queryKey: ["cruise", tripId, cruiseId],
    queryFn: async () => {
      try {
        const { cruiseSearchResult } = await getCruiseSearchResult(tripId, cruiseId)

        if (!cruiseSearchResult) {
          return { status: "notFound", cruiseSearchResult: null, tripSearchId }
        }

        return { status: "success", cruiseSearchResult, tripSearchId }
      } catch (err) {
        console.error(err)
        return { status: "error", cruiseSearchResult: null, tripSearchId }
      }
    },
    initialData: {
      status: "isLoading",
      tripSearchId,
      cruiseSearchResult: null,
    },
    enabled: !!cruiseId,
  })

  useEffect(() => {
    if (!cruiseId) return

    const debouncedSave = debounce((queryResult) => {
      setIsDebounceLoading(false)
      queryClient.setQueryData(["cruise", tripId, cruiseId], queryResult)
    }, 500)

    const unsubscribe = subscribeCruiseSearchResult(
      tripId,
      tripSearchId,
      cruiseId,
      (queryResult: CruiseSearchResultQuery) => {
        setIsDebounceLoading(true)
        debouncedSave(queryResult)
      },
    )

    return () => {
      unsubscribe()
      debouncedSave.cancel()
    }
  }, [tripId, cruiseId])

  return { ...queryData, isDebounceLoading }
}
